import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';

import { TRUE_DARK, TRUE_MUTED } from '../auth/AuthLayout';
import { currency } from '../ui';
import { DEFAULT_BRAND, PublicBrand, fontStack, useFontsStylesheet } from './brand';

/** What api/public/businesses/<slug>/invita/<code>/ publishes (Receipt-Web-Server api/loyalty.py). */
type Invitation = {
  business: { name: string; slug: string; brand?: PublicBrand };
  inviter: { first_name: string };
  available: boolean;
  reason: 'closed' | 'full' | null;
  welcome: { title: string; description: string } | null;
  program: { name: string; min_amount: number; reward_days: number; steps: { visit: number; title: string; description: string }[] } | null;
};

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(word => word[0]).join('').toUpperCase();

/** The sign-up: name, phone and consent to promotions. On success the friend lands on their new card. */
const SignUpForm = ({ invitation, slug, code }: { invitation: Invitation; slug: string; code: string }) => {
  const navigate = useNavigate();
  const brand = invitation.business.brand ?? DEFAULT_BRAND;
  const c = brand.colors;
  const [name, setName] = React.useState('');
  const [lastname, setLastname] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [optIn, setOptIn] = React.useState(false);
  const [website, setWebsite] = React.useState('');
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [sending, setSending] = React.useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setErrors({});
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/public/businesses/${encodeURIComponent(slug)}/invita/${encodeURIComponent(code)}/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, lastname, phone, marketing_opt_in: optIn, website }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 201) {
        navigate(data.card_path, { state: { welcome: true } });
        return;
      }
      if (response.status === 429) setErrors({ detail: 'Demasiados intentos. Espera un momento y vuelve a intentar.' });
      else if (typeof data?.detail === 'string') setErrors({ detail: data.detail });
      else setErrors(Object.fromEntries(Object.entries(data ?? {}).map(([k, v]) => [k, Array.isArray(v) ? v.join(' ') : String(v)])));
    } catch {
      setErrors({ detail: 'No hay conexión. Vuelve a intentar.' });
    } finally {
      setSending(false);
    }
  };

  const field = { '& .MuiOutlinedInput-root': { bgcolor: c.surface, borderRadius: '12px', fontFamily: 'inherit' }, '& label': { fontFamily: 'inherit' } };
  return (
    <Box component="form" onSubmit={submit} noValidate data-testid="invite-form" sx={{ display: 'grid', gap: 1.5, mt: 3 }}>
      <Typography component="h2" sx={{ fontWeight: 800, fontSize: 18, fontFamily: fontStack(brand.fonts.heading), fontStretch: brand.fonts.heading.stretch ?? 'normal' }}>
        Regístrate
      </Typography>
      <TextField label="Nombre" value={name} onChange={e => setName(e.target.value)} required autoComplete="given-name"
        error={!!errors.name} helperText={errors.name} sx={field} slotProps={{ htmlInput: { maxLength: 60 } }} />
      <TextField label="Apellido (opcional)" value={lastname} onChange={e => setLastname(e.target.value)} autoComplete="family-name"
        sx={field} slotProps={{ htmlInput: { maxLength: 60 } }} />
      <TextField label="Celular" value={phone} onChange={e => setPhone(e.target.value)} required autoComplete="tel" type="tel"
        error={!!errors.phone} helperText={errors.phone || 'Con él te identificamos en caja.'} sx={field}
        slotProps={{ htmlInput: { inputMode: 'tel', maxLength: 20 } }} />
      {/* Never shown to people: a bot that fills every field fills this one. */}
      <Box component="input" tabIndex={-1} autoComplete="off" aria-hidden name="website" value={website} onChange={e => setWebsite((e.target as HTMLInputElement).value)}
        sx={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', opacity: 0 }} />
      <FormControlLabel sx={{ alignItems: 'flex-start', m: 0 }}
        control={<Checkbox checked={optIn} onChange={e => setOptIn(e.target.checked)} sx={{ mt: -0.75, color: c.muted, '&.Mui-checked': { color: c.primary } }} />}
        label={<Typography sx={{ fontSize: 14, fontFamily: 'inherit' }}>Quiero recibir promociones de {invitation.business.name} por WhatsApp.</Typography>} />
      {errors.detail && <Typography role="alert" sx={{ color: '#C0392B', fontSize: 14, fontFamily: 'inherit' }}>{errors.detail}</Typography>}
      <Button type="submit" variant="contained" disabled={sending}
        sx={{ py: 1.4, borderRadius: 99, textTransform: 'none', fontWeight: 700, fontSize: 16, fontFamily: 'inherit', bgcolor: c.primary, color: c.on_primary, boxShadow: 'none',
          '&:hover': { bgcolor: c.primary, opacity: 0.9, boxShadow: 'none' } }}>
        {sending ? 'Registrando…' : 'Registrarme y recibir mi regalo'}
      </Button>
      <Typography sx={{ color: c.muted, fontSize: 12.5, fontFamily: 'inherit' }}>
        Tus datos los usa {invitation.business.name} para tu tarjeta de lealtad
        {brand.privacy_url ? <> (<Box component="a" href={brand.privacy_url} target="_blank" rel="noopener" sx={{ color: 'inherit' }}>aviso de privacidad</Box>)</> : null}.
      </Typography>
    </Box>
  );
};

/** receipt.com.mx/<slug>/invita/<code>: a customer's invitation. Their friend sees what they get and signs up. */
const InvitePage = () => {
  const { slug = '', code = '' } = useParams();
  const [invitation, setInvitation] = React.useState<Invitation | null>(null);
  const [state, setState] = React.useState<'loading' | 'found' | 'missing'>('loading');

  React.useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BASE_URL}/api/public/businesses/${encodeURIComponent(slug)}/invita/${encodeURIComponent(code)}/`)
      .then(async response => {
        if (!alive) return;
        if (!response.ok) { setState('missing'); return; }
        setInvitation(await response.json());
        setState('found');
      })
      .catch(() => { if (alive) setState('missing'); });
    return () => { alive = false; };
  }, [slug, code]);

  const brand = invitation?.business.brand ?? DEFAULT_BRAND;
  useFontsStylesheet(brand.fonts_css);

  React.useEffect(() => {
    // A personal link: kept out of search engines.
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex';
    document.head.appendChild(robots);
    return () => { robots.remove(); };
  }, []);

  React.useEffect(() => {
    document.title = invitation ? `${invitation.inviter.first_name} te invita · ${invitation.business.name}` : 'TRUE POS';
  }, [invitation]);

  if (state !== 'found' || !invitation) {
    return (
      <>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: '#F2F5F7', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, py: 6, fontFamily: 'Inter, sans-serif' }}>
          <Box sx={{ width: '100%', maxWidth: 440, bgcolor: '#fff', borderRadius: '20px', boxShadow: '0 8px 30px rgba(20,37,44,0.08)', p: { xs: 4, sm: 5 }, textAlign: 'center' }}>
            {state === 'loading' && <CircularProgress size={32} sx={{ color: TRUE_DARK, my: 6 }} />}
            {state === 'missing' && (
              <>
                <Typography component="h1" sx={{ fontWeight: 700, fontSize: 24, color: TRUE_DARK }}>No encontramos esta invitación</Typography>
                <Typography sx={{ color: TRUE_MUTED, mt: 1.5 }}>Revisa el enlace que te compartieron.</Typography>
              </>
            )}
          </Box>
          <Typography component={Link} to="/" sx={{ mt: 3, color: TRUE_MUTED, fontSize: 13, textDecoration: 'none' }}>Hecho con TRUE POS</Typography>
        </Box>
      </>
    );
  }

  const c = brand.colors;
  const heading = { fontFamily: fontStack(brand.fonts.heading), fontStretch: brand.fonts.heading.stretch ?? 'normal' };
  const { business, inviter, program, welcome } = invitation;
  return (
    <>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: c.background, color: c.text, fontFamily: fontStack(brand.fonts.body), fontStretch: brand.fonts.body.stretch ?? 'normal' }}>
        <Box component="header" sx={{ bgcolor: c.surface, borderBottom: `1px solid ${c.border}` }}>
          <Box sx={{ maxWidth: 560, mx: 'auto', px: 2, py: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            {brand.logo ? (
              <Box component="img" src={brand.logo} alt={business.name} sx={{ height: 56, maxWidth: 180, objectFit: 'contain' }} />
            ) : (
              <>
                <Box sx={{ width: 48, height: 48, borderRadius: '12px', display: 'grid', placeItems: 'center', bgcolor: c.primary }}>
                  <Typography sx={{ color: c.on_primary, fontWeight: 700, ...heading }}>{initials(business.name)}</Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 18, ...heading }}>{business.name}</Typography>
              </>
            )}
          </Box>
        </Box>

        <Box component="main" sx={{ maxWidth: 560, mx: 'auto', px: 2, py: 3 }}>
          <Typography component="h1" sx={{ fontWeight: 800, fontSize: 28, lineHeight: 1.15, ...heading }}>
            {inviter.first_name} te invita a {business.name}
          </Typography>

          {!invitation.available ? (
            <Box data-testid="invite-unavailable" sx={{ mt: 2, p: 2.5, borderRadius: '16px', bgcolor: c.surface, border: `1px solid ${c.border}` }}>
              <Typography sx={{ fontWeight: 700, fontFamily: 'inherit' }}>
                {invitation.reason === 'full' ? `${inviter.first_name} ya usó todas sus invitaciones.` : 'Esta invitación ya no está disponible.'}
              </Typography>
              <Typography sx={{ color: c.muted, mt: 0.5, fontFamily: 'inherit' }}>Visítanos y regístrate en caja con tu teléfono.</Typography>
              <Button component={Link} to={`/${business.slug}`} sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, color: c.text, px: 0, fontFamily: 'inherit' }}>
                Ver {brand.catalog_label || 'el menú'}
              </Button>
            </Box>
          ) : (
            <>
              {welcome && (
                <Box sx={{ mt: 2, p: 2, borderRadius: '16px', bgcolor: c.surface, border: `2px solid ${c.accent}`, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CardGiftcardOutlinedIcon sx={{ color: c.accent, mt: 0.25 }} />
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'inherit' }}>Tu regalo al registrarte</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 18, fontFamily: 'inherit' }}>{welcome.title}</Typography>
                    {welcome.description && <Typography sx={{ color: c.muted, fontSize: 14, fontFamily: 'inherit' }}>{welcome.description}</Typography>}
                    {program && <Typography sx={{ fontSize: 13, mt: 0.5, fontFamily: 'inherit' }}>Úsalo en tu primera compra, dentro de {program.reward_days} días.</Typography>}
                  </Box>
                </Box>
              )}

              <SignUpForm invitation={invitation} slug={slug} code={code} />

              {program && program.steps.length > 0 && (
                <Box component="section" sx={{ mt: 4 }}>
                  <Typography component="h2" sx={{ fontWeight: 800, fontSize: 18, mb: 1, ...heading }}>Y en tus siguientes visitas</Typography>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, bgcolor: c.surface, border: `1px solid ${c.border}`, borderRadius: '16px', overflow: 'hidden' }}>
                    {program.steps.map((step, i) => (
                      <Box component="li" key={step.visit} sx={{ p: 1.5, px: 2, borderTop: i ? `1px solid ${c.border}` : 'none' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 15, fontFamily: 'inherit' }}>Visita {step.visit} · {step.title}</Typography>
                        {step.description && <Typography sx={{ color: c.muted, fontSize: 13.5, fontFamily: 'inherit' }}>{step.description}</Typography>}
                      </Box>
                    ))}
                  </Box>
                  {program.min_amount > 0 && (
                    <Typography sx={{ color: c.muted, fontSize: 13, mt: 1, fontFamily: 'inherit' }}>
                      Cuenta una visita por día, con compra de {currency(program.min_amount)} o más.
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>

        <Box component="footer" sx={{ maxWidth: 560, mx: 'auto', px: 2, pb: 5, pt: 2, display: 'flex', justifyContent: 'center', borderTop: `1px solid ${c.border}` }}>
          <Typography component={Link} to={`/${business.slug}`} sx={{ color: c.muted, fontSize: 13, textDecoration: 'none', fontFamily: 'inherit' }}>
            {business.name} · Hecho con TRUE POS
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default InvitePage;
